<script>
export default {
    name: 'RawDataDisplay',

    props: {
        data: Object,
    },

    computed: {
        meta() {
            return JSON.stringify(this.data, null, ' ').trim();
        },

        metaLines() {
            const metaLines = [];

            if (this.meta) {
                // console.log(this.meta);
                const lines = this.meta.split('\n');
                let indent = 0; // Regular nesting level for margin purposes
                let anonymousIndent = -1; // Indent level when the anonymous object started
                let color = '#ffffff';

                // Throw out the first and last lines because they are just braces
                lines.splice(0, 1);
                lines.splice(lines.length - 1, 1);

                // Loop through our lines and add them to our list
                for (const line of lines) {
                    // If the line contains an opening brace, the next lines will have more indentation
                    // We only display this line up to but not including the brace
                    if (line.includes('{')) {
                        // If the line is only an opening brace, we have a special case where an object is
                        //  inside an array, we do want to draw this one
                        if (line.trim().length === 1) {
                            anonymousIndent = indent;
                            metaLines.push({
                                label: '{',
                                value: '',
                                indent,
                            });
                        } else {
                            const label = line.split(':')[0];
                            metaLines.push({
                                label: `${label.trim().replace(/\"/g, '').replace(',', '')}:`,
                                value: '',
                                indent,
                            });
                        }

                        indent++;
                    } else if (line.includes('}')) {
                        // If the line contains a closing brace, the next lines will have less indentation
                        // We also don't display the closing brace or do anything with the line at all
                        indent--;
                        // We only want to display the brace if we are closing an anonymous object
                        if (anonymousIndent > -1 && indent == anonymousIndent) {
                            metaLines.push({
                                label: '}',
                                value: '',
                                indent,
                            });
                            anonymousIndent = -1;
                        }
                    } else if (line.includes(']')) {
                        indent--;
                        // Special case, close the brackets without using the label styling
                        metaLines.push({
                            label: '',
                            value: ']',
                            indent,
                        });
                    } else {
                        // Regular line, use the indent we have already set
                        const words = line.split(':');
                        // We need this special logic for timestamps that have colons in them
                        const rawValue =
                            words.length > 1
                                ? words.slice(1).join(':')
                                : words[0];
                        const sanitizedValue = rawValue
                            .trim()
                            .replace(/\"/g, '')
                            .replace(',', '');
                        metaLines.push({
                            label:
                                words.length > 1
                                    ? `${words[0].trim().replace(/\"/g, '').replace(',', '')}:`
                                    : '',
                            value: sanitizedValue,
                            indent,
                        });

                        // If we're starting a bracket, we need to increase the indent
                        if (line.includes('[')) {
                            indent++;
                        }
                    }
                }
            }

            return metaLines;
        },
    },

    methods: {
        entriesFromIndent(indent) {
            const entries = [];
            for (let i = 0; i < indent; i++) {
                entries.push(i);
            }
            return entries;
        },
    },
};
</script>

<template>
    <div class="contents">
        <div class="line" v-for="(line, index) in metaLines" :key="index">
            <div
                class="backing"
                v-for="indent in entriesFromIndent(line.indent)"
                :key="indent"
            ></div>
            <div
                class="entry"
                :style="{ 'margin-left': `${line.indent * 20}px` }"
            >
                <span class="label" v-if="line.label">{{ line.label }}</span>
                <span>{{ line.value }}</span>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';
@use '../../../palette.scss' as *;

.log-blob {
    max-height: 600px;
    white-space: pre-wrap;
    font-family: monospace;
    word-break: break-word;
    overflow-y: auto;
}

.contents {
    padding: 10px;
    max-height: 1050px;
    overflow-y: auto;

    .line {
        position: relative;
        width: 100%;
        line-height: 1.5;

        .backing {
            position: absolute;
            width: 100%;
            height: 100%;
            left: 0;
            top: 0;
            background-color: color.adjust(black, $alpha: -0.95);
        }

        .entry {
            display: flex;
            flex-flow: row nowrap;
            color: $theme-dark-grey;

            .label {
                font-weight: 700;
                margin-right: 15px;
            }
        }
    }
}
</style>
